/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Paper,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip,
  Select,
  FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchAssessmentReferenceData,
  checkIsFormless,
} from "../../api/assessment";
import { validateSchemaForm, isFieldVisible } from "./ClinicalNotePage";

import DynamicFormRenderer from "./WomensHealth";

const mapLocalProgramId = (prog) => {
  if (!prog) return "";
  return String(prog).trim();
};

const cleanProgramId = (id) => {
  if (!id) return "";
  return String(id)
    .toUpperCase()
    .replace(/_PROGRAM_SPECIFIC/gi, "")
    .trim();
};

const ProgramSpecific = React.forwardRef(
  (
    {
      memberData,
      selectedProgram,
      assessmentType,
      onFormStateChange,
      secondaryColor,
      borderColor,
      primaryColor,
      programAssessmentSchema,
      programFormPayload,
      columnsCount,
      readOnly = false,
      programMap,
      activePrograms = [],
      setActivePrograms = () => {},
      availablePrograms = [],
      warnings = {},
      schemaLoading = {},
      referenceData: propReferenceData = null,
      activeProgramTab = "",
      onTabChange = () => {},
      onAddProgram,
      onRemoveProgram,
      selectedOutcome = {},
      getOutcomeForProgram = null,
      onAssessmentTypeChange,
    },
    ref,
  ) => {
    const [referenceData, setReferenceData] = useState(
      propReferenceData || null,
    );

    useEffect(() => {
      if (propReferenceData) {
        setReferenceData(propReferenceData);
        return;
      }
      const getRefData = async () => {
        try {
          const data = await fetchAssessmentReferenceData();
          setReferenceData(data);
        } catch (error) {
          console.error(
            "Failed to fetch assessment reference data in ProgramSpecific:",
            error,
          );
        }
      };
      void getRefData();
    }, [propReferenceData]);

    const initialPrograms = Object.entries(programMap || {})
      .filter(([id, name]) => !checkIsFormless({ id, name }, referenceData))
      .map(([id, name]) => ({
        id,
        name,
      }));

    const filteredActivePrograms = React.useMemo(() => {
      return (activePrograms || []).filter(
        (ap) => !checkIsFormless(ap, referenceData),
      );
    }, [activePrograms, referenceData]);

    const [tabValue, setTabValue] = useState(() => {
      if (activeProgramTab) {
        const match = filteredActivePrograms.find(
          (p) =>
            p.id === activeProgramTab ||
            cleanProgramId(p.id) === cleanProgramId(activeProgramTab),
        );
        if (match) return match.id;
      }
      if (
        selectedProgram?.id &&
        filteredActivePrograms.some(
          (p) =>
            p.id === selectedProgram.id ||
            cleanProgramId(p.id) === cleanProgramId(selectedProgram.id),
        )
      ) {
        return selectedProgram.id;
      }
      return filteredActivePrograms[0]?.id || initialPrograms[0]?.id || "";
    });

    const onTabChangeRef = React.useRef(onTabChange);
    useEffect(() => {
      onTabChangeRef.current = onTabChange;
    }, [onTabChange]);

    const prevReportedTabRef = React.useRef(tabValue);

    const changeTab = useCallback((newTab) => {
      setTabValue(newTab);
      if (newTab && newTab !== prevReportedTabRef.current) {
        prevReportedTabRef.current = newTab;
        onTabChangeRef.current?.(newTab);
      }
    }, []);

    useEffect(() => {
      if (activeProgramTab) {
        const match = filteredActivePrograms.find(
          (p) =>
            p.id === activeProgramTab ||
            cleanProgramId(p.id) === cleanProgramId(activeProgramTab),
        );
        if (match && match.id !== tabValue) {
          setTabValue(match.id);
          prevReportedTabRef.current = match.id;
          return;
        }
      }
      if (
        filteredActivePrograms.length > 0 &&
        !filteredActivePrograms.some((p) => p.id === tabValue)
      ) {
        const nextTab = filteredActivePrograms[0].id;
        changeTab(nextTab);
      }
    }, [activeProgramTab, filteredActivePrograms, tabValue, changeTab]);

    useEffect(() => {
      if (tabValue && tabValue !== prevReportedTabRef.current) {
        prevReportedTabRef.current = tabValue;
        onTabChangeRef.current?.(tabValue);
      }
    }, [tabValue]);

    const handleFormStateChange = useCallback(
      (payload) => {
        if (onFormStateChange) {
          onFormStateChange((prev) => ({
            ...prev,
            [tabValue]: payload,
          }));
        }
      },
      [onFormStateChange, tabValue],
    );
    const [addAnchorEl, setAddAnchorEl] = useState(null);

    const handleAddClick = (event) => {
      setAddAnchorEl(event.currentTarget);
    };

    const handleAddClose = () => {
      setAddAnchorEl(null);
    };

    const handleAddProgram = (prog) => {
      if (onAddProgram) {
        onAddProgram(prog);
      } else {
        setActivePrograms((prev) => [...prev, prog]);
      }
      changeTab(prog.id);
      handleAddClose();
    };

    const handleTabChange = (event, newValue) => {
      changeTab(newValue);
    };

    const handleRemoveProgram = (progId, event) => {
      event.stopPropagation();
      if (filteredActivePrograms.length <= 1) return; // Constraint: A minimum of one program must remain.
      if (onRemoveProgram) {
        onRemoveProgram(progId);
      } else {
        const newActive = activePrograms.filter((p) => p.id !== progId);
        setActivePrograms(newActive);
      }
      if (tabValue === progId) {
        const remaining = filteredActivePrograms.find((p) => p.id !== progId);
        changeTab(remaining?.id || "");
      }
    };

    const normalizeProgramString = (str) =>
      String(str || "")
        .toLowerCase()
        .replace(/['\s_progam-]+/gi, "")
        .trim();

    const candidatePrograms = React.useMemo(() => {
      const list = [];
      const seen = new Set();

      const addCandidate = (p) => {
        if (!p) return;
        const rawCode = String(
          p.programId || p.programCode || p.id || "",
        ).trim();
        const rawName = String(
          p.displayName || p.name || p.groupName || p.programName || rawCode,
        ).trim();
        const gType = String(p.groupType || "").toUpperCase();

        if (
          gType === "GENERAL" ||
          gType === "LIFESTYLE" ||
          gType === "CARE_PLAN" ||
          gType === "OTHER"
        ) {
          return;
        }

        // Exclude formless: true
        if (
          p.formless === true ||
          String(p.formless).toLowerCase() === "true" ||
          p.isFormless === true ||
          String(p.isFormless).toLowerCase() === "true"
        ) {
          return;
        }

        const upperCode = rawCode.toUpperCase();
        const upperName = rawName.toUpperCase();
        const lowerName = rawName.toLowerCase();

        if (referenceData?.programs && Array.isArray(referenceData.programs)) {
          const matched = referenceData.programs.find((rp) => {
            const rpCode = String(rp.programCode || rp.programId || rp.id || "")
              .toUpperCase()
              .trim();
            const rpName = String(
              rp.name || rp.displayName || rp.groupName || "",
            )
              .toUpperCase()
              .trim();
            return (
              (upperCode && rpCode && upperCode === rpCode) ||
              (upperName && rpName && upperName === rpName) ||
              (upperCode && rpName && upperCode === rpName) ||
              (upperName && rpCode && upperName === rpCode)
            );
          });
          if (
            matched &&
            (matched.formless === true ||
              String(matched.formless).toLowerCase() === "true" ||
              matched.isFormless === true ||
              String(matched.isFormless).toLowerCase() === "true")
          ) {
            return;
          }
        }

        // Excluding name: Others (and Other)
        if (
          upperName === "OTHERS" ||
          upperName === "OTHER" ||
          upperCode === "OTHERS" ||
          upperCode === "OTHER"
        ) {
          return;
        }

        if (
          upperCode === "GENERAL" ||
          upperCode === "LIFESTYLE" ||
          upperCode === "CARE_PLAN" ||
          upperCode === "METADATA" ||
          lowerName.includes("lifestyle") ||
          lowerName.includes("care plan") ||
          lowerName.includes("careplan") ||
          lowerName.includes("general") ||
          checkIsFormless(
            { id: rawCode, name: rawName, programId: rawCode },
            referenceData,
          )
        ) {
          return;
        }

        const canonicalId = cleanProgramId(rawCode);
        const normKey = normalizeProgramString(canonicalId || rawName);

        if (normKey && !seen.has(normKey)) {
          seen.add(normKey);
          list.push({
            ...p,
            id: canonicalId || rawCode,
            programId: canonicalId || rawCode,
            name: rawName,
            groupName: rawName,
            displayName: p.displayName || rawName,
            programDisplayName: p.displayName || rawName,
            programCode: canonicalId || rawCode,
          });
        }
      };

      const rawPrograms =
        referenceData?.programs && Array.isArray(referenceData.programs)
          ? referenceData.programs
          : availablePrograms || [];

      if (Array.isArray(rawPrograms) && rawPrograms.length > 0) {
        rawPrograms.forEach(addCandidate);
      }
      return list;
    }, [referenceData, availablePrograms]);

    const removedPrograms = candidatePrograms.filter(
      (p) =>
        !filteredActivePrograms.some((ap) => {
          const apId = normalizeProgramString(ap.id || ap.programId);
          const apName = normalizeProgramString(ap.name || ap.displayName);
          const pId = normalizeProgramString(p.id || p.programId);
          const pName = normalizeProgramString(p.name || p.displayName);

          return (
            (apId && (apId === pId || apId === pName)) ||
            (apName && (apName === pId || apName === pName))
          );
        }),
    );

    const [visitedPrograms, setVisitedPrograms] = useState(() => {
      const initialSet = new Set();
      if (tabValue) {
        initialSet.add(tabValue);
        initialSet.add(cleanProgramId(tabValue));
        initialSet.add(normalizeProgramString(tabValue));
      }
      return initialSet;
    });
    const [passedPrograms, setPassedPrograms] = useState(new Set());
    const [hasAttemptedNext, setHasAttemptedNext] = useState(false);

    // Track visited programs when tabValue changes
    useEffect(() => {
      if (tabValue) {
        const cleanId = cleanProgramId(tabValue);
        const normId = normalizeProgramString(tabValue);
        setVisitedPrograms(
          (prev) => new Set([...prev, tabValue, cleanId, normId]),
        );
      }
    }, [tabValue]);

    const isVisitedInVisit = useCallback(
      (progId) => {
        if (!progId) return false;
        const cleanId = cleanProgramId(progId);
        const normId = normalizeProgramString(progId);
        return (
          visitedPrograms.has(progId) ||
          visitedPrograms.has(cleanId) ||
          (normId && visitedPrograms.has(normId))
        );
      },
      [visitedPrograms],
    );

    const isVisitedProgram = useCallback(
      (progId) => {
        if (!progId) return false;
        const cleanId = cleanProgramId(progId);
        const normId = normalizeProgramString(progId);
        const answers =
          programFormPayload?.[cleanId] ||
          programFormPayload?.[progId] ||
          programFormPayload?.[`${cleanId}_PROGRAM_SPECIFIC`];
        const hasAnswers = answers && Object.keys(answers).length > 0;
        return (
          visitedPrograms.has(progId) ||
          visitedPrograms.has(cleanId) ||
          (normId && visitedPrograms.has(normId)) ||
          hasAnswers
        );
      },
      [visitedPrograms, programFormPayload],
    );

    const checkProgramValidation = useCallback(
      (progId) => {
        const cleanId = cleanProgramId(progId);
        const mapKey = `${cleanId}_PROGRAM_SPECIFIC`;
        const schema =
          programAssessmentSchema?.[mapKey] ||
          programAssessmentSchema?.[`${cleanId}_PROGRAM_SPECIFIC`] ||
          programAssessmentSchema?.[progId] ||
          programAssessmentSchema?.[cleanId] ||
          programAssessmentSchema;
        const answers =
          programFormPayload?.[cleanId] ||
          programFormPayload?.[progId] ||
          programFormPayload?.[mapKey] ||
          programFormPayload?.[tabValue] ||
          (cleanId && programFormPayload
            ? Object.entries(programFormPayload).find(
                ([k]) => cleanProgramId(k) === cleanId,
              )?.[1]
            : null) ||
          {};

        // Ensure template schema exists and contains sections for the specific assessment type
        if (schema?.sections && Object.keys(schema.sections).length > 0) {
          // Must pass full schema field validation (all required questions answered)
          return validateSchemaForm(schema, answers, isFieldVisible);
        }
        // If program has no template configured for this assessment type, return false (treated as error program)
        return false;
      },
      [programAssessmentSchema, programFormPayload],
    );

    // Monitor validation passing for each program and update passedPrograms after a 2-second delay
    useEffect(() => {
      const timers = [];
      filteredActivePrograms.forEach((prog) => {
        const isVisited = isVisitedProgram(prog.id);
        const isValid = checkProgramValidation(prog.id);
        const isSuccess = isVisited && isValid;

        if (isSuccess) {
          if (!passedPrograms.has(prog.id)) {
            const timer = setTimeout(() => {
              setPassedPrograms((prev) => new Set([...prev, prog.id]));
            }, 2000);
            timers.push(timer);
          }
        } else if (passedPrograms.has(prog.id)) {
          setPassedPrograms((prev) => {
            const next = new Set(prev);
            next.delete(prog.id);
            return next;
          });
        }
      });

      return () => {
        timers.forEach((t) => clearTimeout(t));
      };
    }, [
      filteredActivePrograms,
      isVisitedProgram,
      checkProgramValidation,
      passedPrograms,
    ]);

    React.useImperativeHandle(ref, () => ({
      navigateToNextProgram: () => {
        setHasAttemptedNext(true);

        const currentIndex = filteredActivePrograms.findIndex(
          (p) =>
            p.id === tabValue ||
            cleanProgramId(p.id) === cleanProgramId(tabValue) ||
            normalizeProgramString(p.id) === normalizeProgramString(tabValue),
        );

        const isLastProgram =
          currentIndex >= 0 &&
          currentIndex === filteredActivePrograms.length - 1;

        // Check if all active programs are visited at least once during this visit
        const allVisited = filteredActivePrograms.every(
          (prog) =>
            isVisitedInVisit(prog.id) || isVisitedInVisit(prog.programId),
        );

        // Check if all active programs have all required questions answered
        const allValid = filteredActivePrograms.every((prog) =>
          checkProgramValidation(prog.id),
        );

        // 3. Once user fills all the required questions and all the active programs
        // are visited once, when user is at the last active program then user should be
        // navigated to Lifestyle, Barriers and Functional Assessment section
        if (allVisited && allValid && isLastProgram) {
          return false; // Tells parent ClinicalNotePage to advance to Lifestyle step
        }

        // 1. When user is NOT at the last active program, clicking next moves to the next active program
        if (
          currentIndex >= 0 &&
          currentIndex < filteredActivePrograms.length - 1
        ) {
          const nextProg = filteredActivePrograms[currentIndex + 1];
          changeTab(nextProg.id);
          return true; // Moved to next active program
        }

        // When user is at the last active program (or single program) but either:
        // - not all active programs have been visited once (Requirement 2), or
        // - not all required questions in all programs have been answered (Requirement 1):
        // Find the first program that needs attention (searching from index 0):
        let targetProgram = filteredActivePrograms.find(
          (prog) =>
            !isVisitedInVisit(prog.id) && !isVisitedInVisit(prog.programId),
        );

        if (!targetProgram) {
          targetProgram = filteredActivePrograms.find(
            (prog) => !checkProgramValidation(prog.id),
          );
        }

        if (!targetProgram && filteredActivePrograms.length > 1) {
          targetProgram = filteredActivePrograms[0];
        }

        if (
          targetProgram &&
          targetProgram.id !== tabValue &&
          cleanProgramId(targetProgram.id) !== cleanProgramId(tabValue)
        ) {
          changeTab(targetProgram.id);
          return true;
        }

        // If targetProgram is the current tab (e.g. only the last program has unanswered questions),
        // we stay on this program so user can answer the required questions.
        return true;
      },
      navigateToPrevProgram: () => {
        const currentIndex = filteredActivePrograms.findIndex(
          (p) =>
            p.id === tabValue ||
            cleanProgramId(p.id) === cleanProgramId(tabValue) ||
            normalizeProgramString(p.id) === normalizeProgramString(tabValue),
        );

        // 1. When user clicks on Prev button, user should be moved to prev Active program
        if (currentIndex > 0) {
          const prevProg = filteredActivePrograms[currentIndex - 1];
          changeTab(prevProg.id);
          return true; // Moved to prev active program
        }

        // When user is at the first active program (currentIndex === 0):
        // 2. Check if all active programs have been visited at least once
        const unvisitedProg = filteredActivePrograms.find(
          (prog) =>
            !isVisitedInVisit(prog.id) && !isVisitedInVisit(prog.programId),
        );

        if (
          unvisitedProg &&
          unvisitedProg.id !== tabValue &&
          cleanProgramId(unvisitedProg.id) !== cleanProgramId(tabValue)
        ) {
          changeTab(unvisitedProg.id);
          return true;
        }

        // 3. Active programs are visited once, when user is at the first active program
        // then user should be navigated to Core Assessment
        return false;
      },
    }));

    return (
      <Box>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: 1,
            mt: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {(() => {
              const currentProg = filteredActivePrograms.find(
                (p) => p.id === tabValue,
              );
              const currentCleanId = currentProg
                ? cleanProgramId(currentProg.id)
                : "";
              const currentMapKey = `${currentCleanId}_PROGRAM_SPECIFIC`;
              const hasCurrentSchema = !!(
                programAssessmentSchema?.[currentMapKey] ||
                programAssessmentSchema?.sections
              );
              const isCurrentLoading = currentProg
                ? !!schemaLoading?.[currentProg.id] ||
                  !!schemaLoading?.[currentMapKey] ||
                  !hasCurrentSchema
                : false;

              const isVisited = currentProg
                ? isVisitedProgram(currentProg.id)
                : false;
              const isValid = currentProg
                ? checkProgramValidation(currentProg.id)
                : true;
              const isCurrentTabFailed =
                !isCurrentLoading && isVisited && !isValid;
              const indicatorColor = isCurrentTabFailed
                ? "#E28B8B"
                : primaryColor;

              return (
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: indicatorColor,
                      transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                    },
                  }}
                  sx={{
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: "14px",
                      px: 0,
                      mr: 3,
                      minWidth: 0,
                      "&.Mui-selected": {
                        fontWeight: 600,
                      },
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: indicatorColor,
                      height: 2,
                      transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                    },
                  }}
                >
                  {filteredActivePrograms.map((prog) => {
                    const cleanedProgId = cleanProgramId(prog.id);
                    const mapKey = `${cleanedProgId}_PROGRAM_SPECIFIC`;
                    const hasLoadedSchema = !!(
                      programAssessmentSchema?.[mapKey] ||
                      programAssessmentSchema?.sections
                    );
                    const isCurrentlyLoading =
                      !!schemaLoading?.[prog.id] ||
                      !!schemaLoading?.[mapKey] ||
                      !hasLoadedSchema;

                    const isVisited = isVisitedProgram(prog.id);
                    const isValid = checkProgramValidation(prog.id);

                    // Keep normal gray/theme color while API/schema is loading or until next is attempted
                    const isFailedValidation =
                      hasAttemptedNext &&
                      !isCurrentlyLoading &&
                      isVisited &&
                      !isValid;

                    let textColor = secondaryColor;

                    if (isFailedValidation) {
                      textColor = "#E28B8B";
                    }

                    const tooltipContent = (
                      <Box sx={{ p: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
                        >
                          Please complete the following:
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block" }}>
                          1. Answer all the required questions for all the
                          Programs.
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block" }}>
                          2. Visit all the program templates
                        </Typography>
                      </Box>
                    );

                    const tabElement = (
                      <Tab
                        key={prog.id}
                        value={prog.id}
                        sx={{
                          color: textColor,
                          "&.Mui-selected": {
                            color: isFailedValidation
                              ? "#E28B8B"
                              : primaryColor,
                          },
                        }}
                        label={
                          isFailedValidation ? (
                            <Tooltip
                              title={tooltipContent}
                              arrow
                              placement="top"
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  width: "100%",
                                  height: "100%",
                                }}
                              >
                                <span style={{ color: "inherit" }}>
                                  {prog.programDisplayName ||
                                    prog.displayName ||
                                    prog.name}
                                </span>
                                {!readOnly &&
                                  filteredActivePrograms.length > 1 && (
                                    <IconButton
                                      size="small"
                                      onClick={(e) =>
                                        handleRemoveProgram(prog.id, e)
                                      }
                                      sx={{ p: 0.2, color: "inherit" }}
                                    >
                                      <CloseIcon sx={{ fontSize: "14px" }} />
                                    </IconButton>
                                  )}
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <span style={{ color: "inherit" }}>
                                {prog.programDisplayName ||
                                  prog.displayName ||
                                  prog.name}
                              </span>
                              {!readOnly &&
                                filteredActivePrograms.length > 1 && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) =>
                                      handleRemoveProgram(prog.id, e)
                                    }
                                    sx={{ p: 0.2, color: "inherit" }}
                                  >
                                    <CloseIcon sx={{ fontSize: "14px" }} />
                                  </IconButton>
                                )}
                            </Box>
                          )
                        }
                      />
                    );

                    return tabElement;
                  })}
                </Tabs>
              );
            })()}

            {!readOnly && removedPrograms.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  onClick={handleAddClick}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    gap: 0.5,
                    color: primaryColor,
                    userSelect: "none",
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      color: "inherit",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "25px",
                        fontWeight: 400,
                        lineHeight: 1,
                      }}
                    >
                      +
                    </span>
                  </IconButton>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: 400,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Add Program
                  </span>
                </Box>
                <Menu
                  anchorEl={addAnchorEl}
                  open={Boolean(addAnchorEl)}
                  onClose={handleAddClose}
                >
                  {removedPrograms.map((prog) => (
                    <MenuItem
                      key={prog.id}
                      onClick={() => handleAddProgram(prog)}
                    >
                      {prog.programDisplayName || prog.displayName || prog.name}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            )}
          </Box>

          {(() => {
            if (!hasAttemptedNext) return null;

            const unvisitedPrograms = filteredActivePrograms.filter(
              (p) => !isVisitedProgram(p.id),
            );
            const invalidVisitedPrograms = filteredActivePrograms.filter(
              (p) => {
                const cleanedId = cleanProgramId(p.id);
                const mapKey = `${cleanedId}_PROGRAM_SPECIFIC`;
                const isCurrentlyLoading =
                  !!schemaLoading?.[p.id] ||
                  !!schemaLoading?.[mapKey] ||
                  (!programAssessmentSchema?.[mapKey] &&
                    !programAssessmentSchema?.[p.id]);
                if (isCurrentlyLoading) return false;
                return isVisitedProgram(p.id) && !checkProgramValidation(p.id);
              },
            );

            const hasUnvisited = unvisitedPrograms.length > 0;
            const hasInvalidVisited = invalidVisitedPrograms.length > 0;

            if (!hasUnvisited && !hasInvalidVisited) return null;

            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                  color: "#E05353",
                  fontSize: "13px",
                  lineHeight: 1.4,
                  userSelect: "none",
                  maxWidth: "480px",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {hasUnvisited && (
                    <span style={{ color: "#E05353", fontWeight: 500 }}>
                      1. Open all program tabs to review templates.
                    </span>
                  )}
                  {hasInvalidVisited && (
                    <span style={{ color: "#E05353", fontWeight: 500 }}>
                      {hasUnvisited && "2. "}Answer all required questions in
                      every program template.
                    </span>
                  )}
                </Box>
              </Box>
            );
          })()}
        </Box>

        {tabValue &&
          (() => {
            const cleanId = cleanProgramId(tabValue);
            const mapKey = `${cleanId}_PROGRAM_SPECIFIC`;
            const isLoading = schemaLoading?.[mapKey];

            if (isLoading) {
              return (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "200px",
                  }}
                >
                  <CircularProgress sx={{ color: primaryColor }} />
                </Box>
              );
            }

            const currentProg = filteredActivePrograms.find(
              (p) =>
                p.id === tabValue ||
                cleanProgramId(p.id) === cleanProgramId(tabValue) ||
                normalizeProgramString(p.id) ===
                  normalizeProgramString(tabValue),
            );
            const currentProgType = String(
              currentProg?.formType ||
                currentProg?.assessmentType ||
                assessmentType ||
                "INITIAL",
            ).toUpperCase();

            const currentSchema =
              programAssessmentSchema?.[mapKey] ||
              programAssessmentSchema?.[cleanId] ||
              programAssessmentSchema?.[tabValue] ||
              (currentProg?.id && programAssessmentSchema?.[currentProg.id]) ||
              (currentProg?.programId &&
                programAssessmentSchema?.[currentProg.programId]) ||
              (currentProg?.name &&
                programAssessmentSchema?.[currentProg.name]) ||
              (programAssessmentSchema?.sections
                ? programAssessmentSchema
                : null);
            const hasSchema =
              currentSchema?.sections &&
              Object.keys(currentSchema.sections).length > 0;

            const warningMessage =
              warnings?.[tabValue] ||
              warnings?.[String(tabValue).toUpperCase()] ||
              warnings?.[String(tabValue).replace("-", "_").toUpperCase()] ||
              warnings?.[cleanProgramId(tabValue)] ||
              warnings?.[mapLocalProgramId(tabValue)];

            const progHeader = (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  mb: 2,
                  mt: 0.5,
                  p: 1.5,
                  backgroundColor: "#F7F8FB",
                  borderRadius: "8px",
                  border: `1px solid ${borderColor}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: secondaryColor,
                        fontWeight: 500,
                      }}
                    >
                      Assessment Type:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <Select
                        disabled={true}
                        value={currentProgType}
                        onChange={(e) =>
                          onAssessmentTypeChange?.(
                            currentProg?.programId ||
                              currentProg?.id ||
                              tabValue,
                            e.target.value,
                          )
                        }
                        MenuProps={{
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                          PaperProps: {
                            sx: {
                              backgroundColor: "#6E6D92",
                              color: "#fff",
                              borderRadius: "10px",
                              boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                              mt: 0.5,
                              boxSizing: "border-box",
                              "& .MuiMenuItem-root": {
                                fontSize: "13px",
                                color: "#fff",
                                py: 1,
                                px: 2,
                                borderRadius: "4px",
                                mx: 0.5,
                                my: 0.25,
                                "&:hover": {
                                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                                },
                                "&.Mui-selected": {
                                  backgroundColor: "#403C6F !important",
                                  fontWeight: 600,
                                  "&:hover": {
                                    backgroundColor: "#403C6F !important",
                                  },
                                },
                              },
                            },
                          },
                        }}
                        sx={{
                          height: "32px",
                          fontSize: "12px",
                          borderRadius: "6px",
                          backgroundColor: "#ECECF1",
                          fontWeight: 600,
                          color: primaryColor,
                          "&.Mui-disabled": {
                            color: "#403C6F",
                            WebkitTextFillColor: "#403C6F",
                            backgroundColor: "#ECECF1",
                            cursor: "not-allowed",
                          },
                          "& .MuiSelect-select.Mui-disabled": {
                            WebkitTextFillColor: "#403C6F",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: borderColor,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: borderColor,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: primaryColor,
                          },
                        }}
                      >
                        <MenuItem value="INITIAL">Initial Assessment</MenuItem>
                        <MenuItem value="FOLLOW_UP">
                          Follow-up Assessment
                        </MenuItem>
                        <MenuItem value="DISCHARGE">
                          Discharge Assessment
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>
            );

            if (!hasSchema) {
              return (
                <Box sx={{}}>
                  {progHeader}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 4,
                      borderRadius: "12px",
                      border: `1px solid ${borderColor}`,
                      textAlign: "center",
                      backgroundColor: "#F7F8FB",
                    }}
                  >
                    <Typography variant="body1" sx={{ color: secondaryColor }}>
                      {warningMessage ||
                        "No assessment template or questions have been configured for the selected program and assessment type. Please contact your administrator."}
                    </Typography>
                  </Paper>
                </Box>
              );
            }

            const isVisited = visitedPrograms.has(tabValue);

            return (
              <Box>
                {progHeader}
                <DynamicFormRenderer
                  key={`${tabValue}_${currentProgType}`}
                  memberData={memberData}
                  onFormStateChange={handleFormStateChange}
                  secondaryColor={secondaryColor}
                  borderColor={borderColor}
                  primaryColor={primaryColor}
                  programAssessmentSchema={currentSchema}
                  programFormPayload={
                    programFormPayload?.[tabValue] || programFormPayload
                  }
                  columnsCount={columnsCount}
                  readOnly={readOnly}
                  showValidationErrors={hasAttemptedNext && isVisited}
                />
              </Box>
            );
          })()}
      </Box>
    );
  },
);

ProgramSpecific.displayName = "ProgramSpecific";

ProgramSpecific.propTypes = {
  memberData: PropTypes.object.isRequired,
  selectedProgram: PropTypes.object,
  assessmentType: PropTypes.string,
  onFormStateChange: PropTypes.func,
  secondaryColor: PropTypes.string.isRequired,
  borderColor: PropTypes.string.isRequired,
  primaryColor: PropTypes.string.isRequired,
  programAssessmentSchema: PropTypes.object,
  programFormPayload: PropTypes.object,
  columnsCount: PropTypes.number,
  readOnly: PropTypes.bool,
  programMap: PropTypes.object,
  activePrograms: PropTypes.array,
  setActivePrograms: PropTypes.func,
  availablePrograms: PropTypes.array,
  warnings: PropTypes.object,
  schemaLoading: PropTypes.object,
  activeProgramTab: PropTypes.string,
  onTabChange: PropTypes.func,
  onAddProgram: PropTypes.func,
  onRemoveProgram: PropTypes.func,
  selectedOutcome: PropTypes.object,
  getOutcomeForProgram: PropTypes.func,
  onAssessmentTypeChange: PropTypes.func,
};

export default ProgramSpecific;
